import { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { RotateCcw, Zap, ChevronLeft, ChevronRight, Pencil, Check, X, Power, MapPin, ExternalLink, RefreshCw, Mail, AlertTriangle } from "lucide-react";
import BONavigation from "../../../components/admin/BONavigation";
import {
    AutoApproveTypeStat,
    AutoApprovedRecord,
    getAutoApproveStats,
    getAutoApproveList,
    revertAutoApprove,
    requeueAutoApprove,
    updateAutoApproveConfig,
} from "../../../interfaces/autoApprove";

function formatDate(iso: string | null): string {
    if (!iso) return "—";
    return new Date(iso).toLocaleString("fr-FR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    });
}

// ── Type stat card ─────────────────────────────────────────────────────────────

function TypeCard({
    stat,
    selected,
    onSelect,
    onConfigSaved,
}: {
    stat: AutoApproveTypeStat;
    selected: boolean;
    onSelect: () => void;
    onConfigSaved: () => void;
}) {
    const [editingLimit, setEditingLimit] = useState(false);
    const [limitInput, setLimitInput] = useState(String(stat.dailyLimit));
    const [saving, setSaving] = useState(false);

    const saveLimit = async () => {
        setSaving(true);
        try {
            await updateAutoApproveConfig(stat.type, { dailyLimit: Number(limitInput) });
            onConfigSaved();
        } finally {
            setSaving(false);
            setEditingLimit(false);
        }
    };

    return (
        <div
            onClick={onSelect}
            className={`bg-white border rounded-xl p-4 cursor-pointer transition-all ${
                selected
                    ? "border-[#1A1A1A] shadow-sm"
                    : "border-gray-200 hover:bg-[#F9F6F3]"
            }`}
        >
            <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                    <Zap className="w-4 h-4 text-gray-400" />
                    <span className="text-sm font-semibold text-gray-900">{stat.label}</span>
                </div>
                {/* The on/off switch lives on /bo/services only — one switch per job. */}
                <a
                    href={`/bo/services${stat.serviceKey ? `/${encodeURIComponent(stat.serviceKey)}` : ""}`}
                    onClick={(e) => e.stopPropagation()}
                    title="L'activation se règle dans Services"
                    className={`flex items-center gap-1 text-xs px-2 py-1 rounded-lg border transition-colors ${
                        stat.enabled
                            ? "border-green-200 bg-green-50 text-green-700 hover:bg-green-100"
                            : "border-gray-200 bg-gray-50 text-gray-500 hover:bg-gray-100"
                    }`}
                >
                    <Power className="w-3 h-3" />
                    {stat.enabled ? "Activé" : "Désactivé"} · Services
                </a>
            </div>

            <div className="grid grid-cols-4 gap-3 mb-3">
                <div>
                    <p className="text-xs text-gray-500">Today</p>
                    <p className="text-2xl font-bold text-gray-900">{stat.approvedToday}</p>
                </div>
                <div>
                    <p className="text-xs text-gray-500">Total</p>
                    <p className="text-2xl font-bold text-gray-900">{stat.approvedTotal}</p>
                </div>
                <div title="Déjà notés « high » — ce que le prochain passage approuverait">
                    <p className="text-xs text-gray-500">Waiting</p>
                    <p className={`text-2xl font-bold ${stat.waitingCount > 0 ? "text-amber-600" : "text-gray-900"}`}>
                        {stat.waitingCount}
                    </p>
                </div>
                <div title="Récupérés mais pas encore notés par l'IA / l'exact-match — la vraie file d'attente en amont">
                    <p className="text-xs text-gray-500">Backlog</p>
                    <p className={`text-2xl font-bold ${(stat.backlogCount ?? 0) > 0 ? "text-blue-600" : "text-gray-900"}`}>
                        {stat.backlogCount ?? 0}
                    </p>
                </div>
            </div>

            {(stat.blockedCount ?? 0) > 0 && (
                <div
                    title="Éligibles à l'approbation, mais rien n'a pu être écrit sur l'établissement (SIREN manquant, payload vide…). Ces enregistrements ne sont PAS approuvés."
                    className="flex items-center gap-1.5 mb-3 px-2 py-1 rounded-lg border border-red-200 bg-red-50"
                >
                    <AlertTriangle className="w-3.5 h-3.5 text-red-600 shrink-0" />
                    <span className="text-xs text-red-700">
                        <span className="font-semibold">{stat.blockedCount}</span> bloqués — rien écrit sur l'établissement
                    </span>
                </div>
            )}

            <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                <span className="text-xs text-gray-500">Daily limit</span>
                {editingLimit ? (
                    <div className="flex items-center gap-1">
                        <input
                            autoFocus
                            type="number"
                            min={0}
                            value={limitInput}
                            onChange={(e) => setLimitInput(e.target.value)}
                            onKeyDown={(e) => { if (e.key === "Enter") saveLimit(); if (e.key === "Escape") setEditingLimit(false); }}
                            className="w-16 text-xs border border-gray-300 rounded px-1.5 py-0.5 focus:outline-none focus:border-[#1A1A1A]"
                        />
                        <button
                            onClick={saveLimit}
                            disabled={saving}
                            className="text-green-600 hover:text-green-700"
                        >
                            <Check className="w-3.5 h-3.5" />
                        </button>
                        <button onClick={() => setEditingLimit(false)} className="text-gray-400 hover:text-gray-600">
                            <X className="w-3.5 h-3.5" />
                        </button>
                    </div>
                ) : (
                    <div className="flex items-center gap-1">
                        <span className="text-xs font-medium text-gray-900">
                            {stat.dailyLimit === 0 ? "Unlimited" : stat.dailyLimit}
                        </span>
                        <button
                            onClick={() => { setLimitInput(String(stat.dailyLimit)); setEditingLimit(true); }}
                            className="text-gray-400 hover:text-gray-600"
                        >
                            <Pencil className="w-3 h-3" />
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}

// ── Records table ──────────────────────────────────────────────────────────────

function RecordsTable({
    type,
    label,
}: {
    type: string;
    label: string;
}) {
    const [records, setRecords] = useState<AutoApprovedRecord[]>([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [loading, setLoading] = useState(false);
    const [reverting, setReverting] = useState<string | null>(null);
    const [requeuing, setRequeuing] = useState(false);
    const [requeueMsg, setRequeueMsg] = useState<string | null>(null);

    const load = useCallback(async (p: number) => {
        setLoading(true);
        try {
            const res = await getAutoApproveList(type, p, 50);
            setRecords(res.data);
            setTotal(res.total);
            setPage(res.page);
            setTotalPages(res.totalPages);
        } finally {
            setLoading(false);
        }
    }, [type]);

    useEffect(() => { load(1); }, [load]);

    const handleRequeue = async () => {
        setRequeuing(true);
        setRequeueMsg(null);
        try {
            const res = await requeueAutoApprove(type);
            setRequeueMsg(res.message);
        } catch {
            setRequeueMsg("Erreur lors du requeue");
        } finally {
            setRequeuing(false);
        }
    };

    const handleRevert = async (recordId: string) => {
        setReverting(recordId);
        try {
            await revertAutoApprove(type, recordId);
            await load(page);
        } finally {
            setReverting(null);
        }
    };

    const isEmailType = type === "website_content_OTS";

    return (
        <div className="bg-white border border-gray-200 rounded-xl">
            <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
                <span className="text-sm font-semibold text-gray-900">
                    {label} auto-approved
                    <span className="ml-2 text-xs font-normal text-gray-500">{total} total</span>
                </span>
                <div className="flex items-center gap-2">
                    {requeueMsg && (
                        <span className="text-xs text-green-600">{requeueMsg}</span>
                    )}
                    {!isEmailType && (
                        <button
                            onClick={handleRequeue}
                            disabled={requeuing}
                            title="Remettre en file pour re-vérification IA (high/medium/low → unverified)"
                            className="flex items-center gap-1 text-xs px-2 py-1 rounded-lg border border-gray-200 text-gray-600 hover:border-amber-300 hover:text-amber-700 hover:bg-amber-50 transition-colors disabled:opacity-40"
                        >
                            <RefreshCw className={`w-3 h-3 ${requeuing ? "animate-spin" : ""}`} />
                            Drop queue
                        </button>
                    )}
                </div>
            </div>

            {loading ? (
                <div className="py-12 text-center text-sm text-gray-400">Loading…</div>
            ) : records.length === 0 ? (
                <div className="py-12 text-center text-sm text-gray-400">No auto-approved records</div>
            ) : (
                <table className="w-full text-sm">
                    <thead>
                        <tr className="border-b border-gray-100">
                            <th className="text-left px-4 py-2 text-xs text-gray-500 font-medium">Name</th>
                            <th className="text-left px-4 py-2 text-xs text-gray-500 font-medium">
                                {isEmailType ? "Email" : "Matched as"}
                            </th>
                            {isEmailType && (
                                <th className="text-left px-4 py-2 text-xs text-gray-500 font-medium">Score</th>
                            )}
                            {!isEmailType && (
                                <th className="text-left px-4 py-2 text-xs text-gray-500 font-medium">Habitants</th>
                            )}
                            <th className="text-left px-4 py-2 text-xs text-gray-500 font-medium">Appliqué</th>
                            <th className="text-left px-4 py-2 text-xs text-gray-500 font-medium">Approved at</th>
                            <th className="px-4 py-2" />
                        </tr>
                    </thead>
                    <tbody>
                        {records.map((r) => (
                            <tr key={r.recordId} className="border-b border-gray-50 hover:bg-[#F9F6F3]">
                                <td className="px-4 py-2.5">
                                    <div className="flex items-center gap-2">
                                        <div>
                                            <span className="text-gray-900 font-medium">{r.name}</span>
                                            {r.commercialName && (
                                                <p className="text-xs text-gray-500">{r.commercialName}</p>
                                            )}
                                            {r.eventAddress && (
                                                <p className="text-xs text-gray-400">
                                                    {r.eventAddress}
                                                    {r.cityPopulation != null && (
                                                        <span className="ml-1.5 text-gray-400">· {r.cityPopulation.toLocaleString("fr-FR")} hab.</span>
                                                    )}
                                                </p>
                                            )}
                                        </div>
                                        {r.siren && (
                                            <a
                                                href={`https://www.pappers.fr/recherche?q=${r.siren}`}
                                                target="_blank"
                                                rel="noreferrer"
                                                title={`Voir sur Pappers (${r.siren})`}
                                                className="flex items-center gap-1 text-xs px-1.5 py-0.5 rounded border border-gray-200 text-gray-500 hover:border-blue-300 hover:text-blue-700 hover:bg-blue-50 transition-colors shrink-0"
                                            >
                                                <ExternalLink className="w-3 h-3" />
                                                Pappers
                                            </a>
                                        )}
                                        {r.establishmentId && (
                                            <a
                                                href={`/establishments/${r.establishmentId}`}
                                                target="_blank"
                                                rel="noreferrer"
                                                title="Voir la fiche établissement"
                                                className="flex items-center gap-1 text-xs px-1.5 py-0.5 rounded border border-gray-200 text-gray-500 hover:border-gray-400 hover:text-gray-800 hover:bg-gray-100 transition-colors shrink-0"
                                            >
                                                <ExternalLink className="w-3 h-3" />
                                                Fiche
                                            </a>
                                        )}
                                    </div>
                                </td>
                                <td className="px-4 py-2.5">
                                    {isEmailType ? (
                                        r.displayName ? (
                                            <div className="flex items-center gap-2">
                                                <Mail className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                                                <div>
                                                    <span className="text-xs text-gray-700 font-medium font-mono">{r.displayName}</span>
                                                    {r.placeAddress === "Owner match" && (
                                                        <p className="text-xs text-green-600 font-medium">Owner match</p>
                                                    )}
                                                    {r.fromEstablishment && (
                                                        <p className="text-[10px] text-gray-400" title="La valeur approuvée n'a pas été conservée : voici ce que la fiche contient aujourd'hui.">
                                                            valeur actuelle de la fiche
                                                        </p>
                                                    )}
                                                </div>
                                            </div>
                                        ) : (
                                            <span className="text-gray-400 text-xs">—</span>
                                        )
                                    ) : r.displayName || r.placeAddress ? (
                                        // No Google name on rows filled from the establishment — it never
                                        // reached the fiche — so the address alone must still show.
                                        <div className="flex items-center gap-2">
                                            <div>
                                                {r.displayName && <span className="text-xs text-gray-700 font-medium">{r.displayName}</span>}
                                                {r.fromEstablishment && (
                                                    <p className="text-[10px] text-gray-400" title="La valeur approuvée n'a pas été conservée : voici ce que la fiche contient aujourd'hui.">
                                                        valeurs actuelles de la fiche
                                                    </p>
                                                )}
                                                {r.placeAddress && (
                                                    <p className="text-xs text-gray-400">{r.placeAddress}</p>
                                                )}
                                            </div>
                                            {r.dataUrl && (
                                                <a
                                                    href={r.dataUrl}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                    title="Ouvrir sur Google Maps"
                                                    className="flex items-center gap-1 text-xs px-1.5 py-0.5 rounded border border-gray-200 text-gray-500 hover:border-green-300 hover:text-green-700 hover:bg-green-50 transition-colors"
                                                >
                                                    <MapPin className="w-3 h-3" />
                                                    Maps
                                                </a>
                                            )}
                                        </div>
                                    ) : r.dataUrl ? (
                                        <a
                                            href={r.dataUrl}
                                            target="_blank"
                                            rel="noreferrer"
                                            className="text-xs text-gray-500 hover:text-gray-800 hover:underline inline-flex items-center gap-1"
                                        >
                                            {r.dataUrl.replace("https://www.linkedin.com/in/", "").replace(/\/$/, "")}
                                            <ExternalLink className="w-3 h-3" />
                                        </a>
                                    ) : (
                                        <span className="text-gray-400 text-xs">—</span>
                                    )}
                                </td>
                                {isEmailType && (
                                    <td className="px-4 py-2.5">
                                        {r.score != null ? (
                                            <span className={`text-xs font-semibold px-1.5 py-0.5 rounded ${
                                                r.score >= 90 ? "bg-green-100 text-green-700" :
                                                r.score >= 70 ? "bg-yellow-100 text-yellow-700" :
                                                "bg-gray-100 text-gray-600"
                                            }`}>
                                                {r.score}
                                            </span>
                                        ) : (
                                            <span className="text-gray-400 text-xs">—</span>
                                        )}
                                    </td>
                                )}
                                {!isEmailType && (
                                    <td className="px-4 py-2.5">
                                        {r.cityPopulation != null ? (
                                            <span className={`text-xs font-semibold ${r.cityPopulation < 20000 ? "text-green-600" : "text-red-500"}`}>
                                                {r.cityPopulation.toLocaleString("fr-FR")}
                                            </span>
                                        ) : (
                                            <span className="text-gray-400 text-xs">—</span>
                                        )}
                                    </td>
                                )}
                                <td className="px-4 py-2.5">
                                    {r.appliedToEstablishment === true ? (
                                        <span
                                            title={
                                                r.appliedPaths?.length
                                                    ? `Champs écrits : ${r.appliedPaths.join(", ")}`
                                                    : "Écrit sur l'établissement"
                                            }
                                            className="inline-flex items-center gap-1 text-xs px-1.5 py-0.5 rounded border border-green-200 bg-green-50 text-green-700"
                                        >
                                            <Check className="w-3 h-3" />
                                            {r.appliedPaths?.length ?? 0} champ{(r.appliedPaths?.length ?? 0) > 1 ? "s" : ""}
                                        </span>
                                    ) : r.appliedToEstablishment === false ? (
                                        <span
                                            title="Approuvé mais rien n'a été écrit sur l'établissement"
                                            className="inline-flex items-center gap-1 text-xs px-1.5 py-0.5 rounded border border-red-200 bg-red-50 text-red-700"
                                        >
                                            <AlertTriangle className="w-3 h-3" />
                                            Rien écrit
                                        </span>
                                    ) : (
                                        <span
                                            title="Approuvé avant la mise en place du suivi — impossible de savoir si les données ont été écrites"
                                            className="text-xs text-gray-400"
                                        >
                                            inconnu
                                        </span>
                                    )}
                                </td>
                                <td className="px-4 py-2.5 text-xs text-gray-500">
                                    {formatDate(r.reviewedAt)}
                                    {!isEmailType && (
                                        <span
                                            title={r.isExactMatch ? "Approuvé par correspondance exacte" : "Approuvé par l'IA"}
                                            className="ml-1.5 text-[10px] text-gray-400"
                                        >
                                            {r.isExactMatch ? "exact" : "IA"}
                                        </span>
                                    )}
                                </td>
                                <td className="px-4 py-2.5 text-right">
                                    <button
                                        onClick={() => handleRevert(r.recordId)}
                                        disabled={reverting === r.recordId}
                                        className="flex items-center gap-1 text-xs px-2 py-1 rounded-lg border border-gray-200 text-gray-600 hover:border-red-300 hover:text-red-600 hover:bg-red-50 transition-colors disabled:opacity-40"
                                    >
                                        <RotateCcw className="w-3 h-3" />
                                        Revert
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}

            {totalPages > 1 && (
                <div className="px-4 py-3 border-t border-gray-100 flex items-center justify-between">
                    <span className="text-xs text-gray-500">Page {page} / {totalPages}</span>
                    <div className="flex gap-1">
                        <button
                            onClick={() => load(page - 1)}
                            disabled={page === 1}
                            className="p-1 rounded border border-gray-200 text-gray-500 hover:bg-gray-50 disabled:opacity-40"
                        >
                            <ChevronLeft className="w-4 h-4" />
                        </button>
                        <button
                            onClick={() => load(page + 1)}
                            disabled={page === totalPages}
                            className="p-1 rounded border border-gray-200 text-gray-500 hover:bg-gray-50 disabled:opacity-40"
                        >
                            <ChevronRight className="w-4 h-4" />
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}

// ── Page ───────────────────────────────────────────────────────────────────────

export default function AutoApprove() {
    const [searchParams] = useSearchParams();
    const typeFromUrl = searchParams.get("type");
    const [stats, setStats] = useState<AutoApproveTypeStat[]>([]);
    const [selectedType, setSelectedType] = useState<string | null>(typeFromUrl);

    const loadStats = useCallback(async () => {
        const data = await getAutoApproveStats();
        setStats(data);
        if (!selectedType && data.length > 0) setSelectedType(data[0].type);
    }, [selectedType]);

    useEffect(() => { loadStats(); }, [loadStats]);

    const selected = stats.find((s) => s.type === selectedType) ?? null;

    return (
        <div className="min-h-screen bg-[#F5F0EB]">
            <div className="mx-auto max-w-7xl px-4">
                <div className="mb-6">
                    <h1 className="text-2xl font-semibold text-gray-900">Auto-Approve</h1>
                    <p className="text-sm text-gray-500 mt-0.5">Monitor and revert automated enrichment approvals</p>
                    <p className="text-xs text-gray-400 mt-1">
                        Cette page ne liste que les enregistrements <strong>approuvés automatiquement</strong>.
                        Les « medium » et « low » en attente de validation manuelle se trouvent dans{" "}
                        <a href="/bo/enrichments" className="underline hover:text-gray-600">
                            Enrichments
                        </a>
                        .
                    </p>
                </div>

                <BONavigation />

                {/* Type cards */}
                <div className="grid grid-cols-3 gap-4 mb-6">
                    {stats.map((stat) => (
                        <TypeCard
                            key={stat.type}
                            stat={stat}
                            selected={selectedType === stat.type}
                            onSelect={() => setSelectedType(stat.type)}
                            onConfigSaved={loadStats}
                        />
                    ))}
                    {stats.length === 0 && (
                        <div className="col-span-3 text-sm text-gray-400">Loading…</div>
                    )}
                </div>

                {/* Records table for selected type */}
                {selected && (
                    <RecordsTable type={selected.type} label={selected.label} />
                )}
            </div>
        </div>
    );
}
